import React, { ReactNode, useEffect, useState } from "react";
import {
  useLocalVideo,
  useLogger,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../video/providers/DevicesProvider";
import { Video, VideoOff } from "lucide-react";
import { isOptionActive } from "../../../video/utils/device-utils";
import { DropdownButtonItem } from "@themes/pollinator/ui/DropdownButtonItem";
import { ButtonDropdownCombo } from "@themes/pollinator/ui/ButtonDropdownCombo";
import { VideoUIContainerBreakpoints } from "../../videoUI.types";
import { videoEventBus } from "../../../video/videoEventBus";
import { useVideoStreamManager } from "../../../video/hooks/useVideoStreamManager";
import { useBrowserCompatibilityFeatures } from "../../../video/hooks/useBrowserCompatibilityFeatures";

interface Props {
  /** The label that will be shown for video input control, it defaults to `Video`. */
  label?: string;
  containerBreakpoints: VideoUIContainerBreakpoints;
}

export const VideoInputControl: React.FC<React.PropsWithChildren<Props>> = ({
  label = "Video",
  containerBreakpoints,
  ...rest
}) => {
  const logger = useLogger();
  const meetingManager = useMeetingManager();
  const { 
    videoInputDevices: devices, 
    selectedVideoInputDevice: selectedDevice,
    selectVideoInputDevice
  } = useDevices();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { startVideoStream, stopVideoStream } = useVideoStreamManager();
  const [dropdownOptions, setDropdownOptions] = useState<ReactNode[]>([]);
  
  // Get browser compatibility features
  const { supportsVideo } = useBrowserCompatibilityFeatures();


  useEffect(() => {
    const handleClick = async (deviceId: string): Promise<void> => {
      try {
        // Update our custom device provider
        await selectVideoInputDevice(deviceId);
        
        // Also update the Chime SDK
        if (isVideoEnabled) {
          await meetingManager.startVideoInputDevice(deviceId);
        } else {
          meetingManager.selectVideoInputDevice(deviceId);
        }
        
        videoEventBus.emitEvent("videochat:video:source:selected", { deviceId });
        console.log('Selected video input device on video screen:', deviceId);
      } catch (error) {
        logger.error("VideoInputControl failed to select video input device");
      }
    };

    const getDropdownOptions = async (): Promise<void> => {
      const dropdownOptions = await Promise.all(
        devices.map(async (device) => (
          <DropdownButtonItem
            key={device.deviceId}
            checked={await isOptionActive(selectedDevice, device.deviceId)}
            onClick={async () => await handleClick(device.deviceId)}
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );
      setDropdownOptions(dropdownOptions);
    };

    getDropdownOptions();
  }, [
    devices,
    selectedDevice,
    isVideoEnabled,
    meetingManager,
    meetingManager.startVideoInputDevice,
    selectVideoInputDevice,
  ]);

  const handleToggleVideo = async () => {
    try {
      // Track the event BEFORE toggling (based on current state)
      if (isVideoEnabled) {
        videoEventBus.emitEvent("videochat:camera:off", {});
        
        // Stop the video stream to release the camera
        await stopVideoStream();
      } else {
        videoEventBus.emitEvent("videochat:camera:on", {});
        
        // Start the video stream with the selected device
        if (selectedDevice) {
          await startVideoStream(selectedDevice.deviceId);
        }
      }
      
      // Then toggle the video state
      await toggleVideo();
    } catch (error) {
      logger.error("VideoInputControl failed to toggle video", error);
    }
  };

  return (
    <ButtonDropdownCombo
      buttonIcon={isVideoEnabled ? Video : VideoOff}
      buttonLabel={
        containerBreakpoints && containerBreakpoints.isMd ? label : ""
      }
      buttonOnClick={supportsVideo ? handleToggleVideo : () => {}}
      dropdownOptions={supportsVideo ? (dropdownOptions || []) : []}
      destructive={!isVideoEnabled}
      disabled={!supportsVideo}
      {...rest}
    />
  );
};

export default VideoInputControl;
